import React, { useMemo } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'

import CONSTANTS from 'helpers/constants'
import { IMAGE_MODERATION_STATUSES } from 'constants/media'
import { USER_DATA_LOCATION } from 'constants/profile'

import { CloseIcon, InputImage, PlusIcon } from 'components'
import WarningDot from 'components/Profile/WarningDot'
import AlertCircleIcon from 'components/AlertCircleIcon'

const { WIDTH, HEIGHT } = CONSTANTS.USER.PROFILE_IMG
const { USER_IMAGES } = USER_DATA_LOCATION

const ImageUploadButton = ({ onChange }) => (
  <>
    <InputImage width={WIDTH} height={HEIGHT} onChange={onChange} autoCrop={true} />
    <div
      className="w-6 h-6 flex items-center justify-center rounded-full"
      style={{ background: 'linear-gradient(320.77deg, #4F5AB3 27.18%, #FFFFFF 289.83%)' }}
    >
      <PlusIcon className="text-white w-4 h-4" />
    </div>
  </>
)

const ProfileImagesInput = ({ user, onImageUpdate, classNames, hideRequiredImages }) => {
  const { hasAllRequiredImages, images, imgArrayIsEmpty } = useMemo(() => {
    const images = user.images?.sort(a => (a.profileImage ? -1 : 0)) || []
    const imgArrayIsEmpty = !images.find(({ url }) => url)
    const hasAllRequiredImages = images.filter(({ url }) => url).length >= 3

    return { images, imgArrayIsEmpty, hasAllRequiredImages }
  }, [user])

  return (
    <div>
      <div
        className={classnames(
          'flex overflow-x-auto items-stretch scrollbar-hidden md:flex-wrap',
          classNames.container
        )}
        style={imgArrayIsEmpty ? { minHeight: '120px' } : {}}
      >
        {images.map(({ id, url, moderationStatus, rejectionReason }, index) => (
          <div
            className={classnames(
              'w-20 flex-shrink-0 mr-5 bg-transparent focus:outline-none relative group md:order-none md:w-24 md:h-36 md:mb-3',
              {
                'order-1': url,
                'order-2': !url,
              },
              classNames.image
            )}
            key={id}
          >
            {url ? (
              <>
                <img className="w-full object-cover rounded-lg h-full" src={url} alt={id} />
                {moderationStatus === IMAGE_MODERATION_STATUSES.REJECTED && (
                  <div className="w-full h-full bg-white bg-opacity-70 rounded-lg text-sm text-center p-2 absolute top-0 left-0">
                    <AlertCircleIcon className="w-4 text-red-500" />
                    <div className="font-bold mb-0.5 -mt-1 text-red-600 md:mb-2">Rejected</div>
                    <div className="font-semibold text-xs md:text-sm">{rejectionReason}</div>
                  </div>
                )}
                <div className="absolute w-full h-full inset-0 justify-center items-center hidden group-hover:flex">
                  <button
                    className="bg-gray-500 bg-opacity-80 w-5 h-5 rounded-full flex justify-center items-center cursor-pointer focus:outline-none"
                    onClick={() =>
                      index > 0 && onImageUpdate({ type: USER_IMAGES, id, image: null })
                    }
                  >
                    {index === 0 ? (
                      <ImageUploadButton
                        onChange={image => onImageUpdate({ type: USER_IMAGES, id, image })}
                      />
                    ) : (
                      <CloseIcon className="text-white w-1.5 h-1.5" />
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="relative bg-woodsmokeBlack w-full border-dashed border-2 h-full rounded-lg border-white border-opacity-10 flex justify-center items-center focus:outline-none hover:opacity-60">
                <ImageUploadButton
                  onChange={image => onImageUpdate({ type: USER_IMAGES, id, image })}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      {!hideRequiredImages && !hasAllRequiredImages && (
        <div className="flex items-center text-xs text-rose font-bold mt-2 md:mt-0">
          <WarningDot className="mr-2.5" />
          <span>
            Submit at least <u>3</u> photos
          </span>
        </div>
      )}
    </div>
  )
}

ProfileImagesInput.propTypes = {
  user: PropTypes.object.isRequired,
  onImageUpdate: PropTypes.func.isRequired,
  classNames: PropTypes.object,
  hideRequiredImages: PropTypes.bool,
}

ProfileImagesInput.defaultProps = {
  classNames: {},
}

export default ProfileImagesInput
